import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Modal, Nav, Spinner } from 'react-bootstrap';
import { ArrowDownUp, ChatSquareText, Eye, GripVertical, Pen, Plus, Search } from 'react-bootstrap-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import Loader from '../Loader';
import ConversationBankFlow from '../conversation-bank/ConversationBankFlow';
import '../conversation-bank/conversationBank.scss';

const ConversationBankAdmin = () => {
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [topicCategory, setTopicCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [showArrange, setShowArrange] = useState(false);
  const [arrangeItems, setArrangeItems] = useState([]);
  const [arrangeLoading, setArrangeLoading] = useState(false);
  const [savingArrange, setSavingArrange] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONVERSATION_BANK_SEARCH,
      requestBody: {
        query,
        topicCategory,
        includeInactive: true,
        mode: 'admin'
      }
    });
    setLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    const records = response.results || [];
    setCategories(response.categories || []);
    setResults(
      statusFilter ? records.filter(record => record.status === statusFilter) : records
    );
  };

  useEffect(() => {
    const handle = setTimeout(fetchList, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, topicCategory, statusFilter]);

  const openArrange = async () => {
    setShowArrange(true);
    setArrangeLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONVERSATION_BANK_SEARCH,
      requestBody: {
        includeInactive: true,
        mode: 'admin'
      }
    });
    setArrangeLoading(false);
    if (error) {
      toast.error(error);
      setShowArrange(false);
      return;
    }
    setArrangeItems(response.results || []);
  };

  const closeArrange = () => {
    if (savingArrange) return;
    setShowArrange(false);
    setArrangeItems([]);
  };

  const onArrangeDragEnd = result => {
    if (!result.destination || result.destination.index === result.source.index) return;
    setArrangeItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  };

  const saveArrange = async () => {
    const ids = arrangeItems.map(record => String(record._id || '')).filter(Boolean);
    if (ids.length < 2) return;
    setSavingArrange(true);
    const { error } = await makeApiRequests({
      method: 'PUT',
      endpoint: ENDPOINTS.CONVERSATION_BANK_REORDER,
      requestBody: { ids }
    });
    setSavingArrange(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success('Conversation order updated');
    setShowArrange(false);
    setArrangeItems([]);
    fetchList();
  };

  const openPreview = async id => {
    const { response, error } = await makeApiRequests({
      endpoint: `${ENDPOINTS.CONVERSATION_BANK_WITH_ID(id)}?includeInactive=true`,
      method: 'GET'
    });
    if (error) {
      toast.error(error);
      return;
    }
    setPreviewRecord(response);
  };

  const updatedLabel = record => {
    const name = record.updatedBy?.name || record.createdBy?.name || 'Unknown';
    const when = record.updatedAt || record.createdAt;
    return `${name} · ${when ? new Date(when).toLocaleString() : ''}`;
  };

  const hasFilters = Boolean(query.trim() || topicCategory || statusFilter);

  return (
    <div className="container py-4" style={{ maxWidth: 960, minHeight: 'calc(100vh - 56px)' }}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
        <div>
          <h2 className="mb-1 d-flex align-items-center">
            <ChatSquareText className="mr-2 text-primary" size={28} />
            Manage Conversation Bank
          </h2>
          <p className="text-muted mb-0">
            Write the language salespeople copy during a live conversation, and keep it in the order they should see it.
          </p>
        </div>
        <div className="d-flex align-items-center flex-wrap mt-3 mt-md-0" style={{ gap: '0.5rem' }}>
          <Button variant="primary" onClick={() => history.push('/admin/conversation-bank/new')}>
            <Plus className="mr-2" size={16} />
            Add conversation
          </Button>
          <Button
            variant="outline-primary"
            onClick={openArrange}
            disabled={Boolean(query.trim())}
            title={query.trim() ? 'Clear search to arrange all conversations.' : ''}
          >
            <ArrowDownUp className="mr-2" size={16} />
            Arrange
          </Button>
        </div>
      </div>

      <div className="d-flex flex-column flex-md-row mb-3" style={{ gap: '0.75rem' }}>
        <div className="position-relative flex-grow-1">
          <Search
            className="position-absolute text-muted"
            style={{ left: 12, top: '50%', transform: 'translateY(-50%)' }}
            size={18}
          />
          <Form.Control
            type="search"
            className="pl-5"
            style={{ borderRadius: '1.2rem' }}
            placeholder="Search conversations..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <Form.Control
          as="select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ borderRadius: '1.2rem', maxWidth: 220 }}
        >
          <option value="">Active and inactive</option>
          <option value="Active">Active only</option>
          <option value="Inactive">Inactive only</option>
        </Form.Control>
      </div>

      <Nav
        variant="tabs"
        className="cb-admin-tabs mb-4"
        activeKey={topicCategory || 'all'}
        onSelect={key => {
          if (key === null || key === undefined) return;
          setTopicCategory(key === 'all' ? '' : key);
        }}
      >
        <Nav.Item>
          <Nav.Link eventKey="all">All</Nav.Link>
        </Nav.Item>
        {categories.map(category => (
          <Nav.Item key={category}>
            <Nav.Link eventKey={category}>{category}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5 text-muted">
          <Spinner animation="border" size="sm" className="mr-2" />
          <span>Loading conversations…</span>
        </div>
      ) : results.length === 0 ? (
        <div className="text-center text-muted py-5 px-3">
          <p className="mb-1 font-weight-medium text-dark">
            {hasFilters ? 'No conversations found' : 'No conversations yet'}
          </p>
          <p className="small mb-0">
            {hasFilters ? 'Try a different search or clear the filter.' : 'Add a conversation to get started.'}
          </p>
        </div>
      ) : (
        results.map(record => {
          const replyCount = (record.branches || []).length;
          return (
            <Card key={record._id} className="mb-3 shadow-sm overflow-hidden">
              <Card.Body>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
                  <div className="flex-grow-1 min-w-0 pr-md-3 w-100">
                    <Card.Title className="h5 mb-0">{record.customerQuestionSituation}</Card.Title>
                    {record.scenarioName ? (
                      <Card.Text className="text-muted small mb-1 mt-1">{record.scenarioName}</Card.Text>
                    ) : null}
                    <Card.Text className="text-muted small mb-0 mt-2">
                      {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                    </Card.Text>
                  </div>
                  <div
                    className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center mt-3 mt-md-0 flex-shrink-0"
                    style={{ gap: '0.5rem' }}
                  >
                    <Button
                      variant="outline-primary"
                      className="text-nowrap d-inline-flex align-items-center"
                      onClick={() => history.push(`/admin/conversation-bank/${record._id}`)}
                    >
                      <Pen className="mr-2" size={16} />
                      Edit
                    </Button>
                    <Button
                      variant="primary"
                      className="p-2 d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded"
                      style={{ lineHeight: 1 }}
                      title="Preview"
                      aria-label="Preview"
                      onClick={() => openPreview(record._id)}
                    >
                      <Eye size={18} />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 w-100 d-flex flex-wrap align-items-center small text-muted" style={{ gap: '0.35rem' }}>
                  <span>Updated {updatedLabel(record)}</span>
                  {record.topicCategory ? (
                    <span
                      className="font-weight-medium px-2 py-1"
                      style={{
                        backgroundColor: '#e9ecef',
                        color: '#495057',
                        borderRadius: '1rem',
                        lineHeight: 1.25
                      }}
                    >
                      {record.topicCategory}
                    </span>
                  ) : null}
                  <span
                    className="font-weight-medium px-2 py-1"
                    style={{
                      backgroundColor: record.status === 'Active' ? '#d4edda' : '#e9ecef',
                      color: record.status === 'Active' ? '#155724' : '#495057',
                      borderRadius: '1rem',
                      lineHeight: 1.25
                    }}
                  >
                    {record.status}
                  </span>
                </div>
              </Card.Body>
            </Card>
          );
        })
      )}

      <Modal show={showArrange} onHide={closeArrange} centered backdrop="static" size="lg">
        <Modal.Header closeButton className="bg-primary text-white border-0">
          <Modal.Title>
            <div className="font-weight-bold">Arrange conversations</div>
            <div className="small font-weight-normal mt-1" style={{ opacity: 0.9 }}>
              Drag to set the order. New conversations are added at the bottom.
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {arrangeLoading ? (
            <Loader />
          ) : arrangeItems.length === 0 ? (
            <div className="text-muted small">No conversations yet.</div>
          ) : (
            <DragDropContext onDragEnd={onArrangeDragEnd}>
              <Droppable droppableId="conversation-arrange-list">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="border rounded p-2"
                    style={{
                      backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : '#fff',
                      maxHeight: 490,
                      overflowY: 'auto'
                    }}
                  >
                    {arrangeItems.map((record, index) => (
                      <Draggable key={String(record._id)} draggableId={String(record._id)} index={index}>
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className="d-flex align-items-center border rounded px-3 py-2 mb-2"
                            style={{
                              backgroundColor: dragSnapshot.isDragging ? '#f1f3f5' : '#fff',
                              ...dragProvided.draggableProps.style
                            }}
                          >
                            <GripVertical className="text-muted flex-shrink-0 mr-2" size={16} />
                            <span className="text-muted small font-weight-bold mr-2">{index + 1}.</span>
                            <span className="text-truncate">
                              {record.customerQuestionSituation || record.scenarioName || 'Untitled conversation'}
                            </span>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="outline-secondary" onClick={closeArrange} disabled={savingArrange}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveArrange} disabled={savingArrange || arrangeItems.length < 2}>
            {savingArrange ? 'Saving order…' : 'Save order'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(previewRecord)} onHide={() => setPreviewRecord(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0">Salesperson preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewRecord && <ConversationBankFlow record={previewRecord} placeholderContext={{}} />}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ConversationBankAdmin;
